import { jsx, css as CSS } from '@emotion/react';
import { get } from 'lodash';
import { Fragment, useState, useEffect } from 'react';

import { ConfigurationConnector } from './BYRConnectors';
import { WelcomeModal } from './WelcomeModal.jsx';
import { logChangeStep } from './analytics';
import { model3dChangeStep } from './model3dBridge';
import { getNextStep } from './steps_order';
import css from './styles_step';
import { MoveNext, HeaderIcon, PriceWidget, If, IfElse } from './utilities';
import AlertMessage from '../OC/oc-alert-message';
import { altText } from '../Roofing/Shingles/imageAlts';
import { PrefixWithLocale } from '../Utilities';
import { dataTrackAttribute } from '../helpers/analytics';
import { connectLocation } from '../location/LocationConnectors';

const unavailableShingleAlertCSS = CSS`
  > span {
    display: inline;
    font-size: 12px;

    &:before {
      top: 2px;
      font-size: 16px;
    }
  }
`;

export const rawSelectShingles = ({
  t,
  shingles,
  configuration = {},
  selected_shingle = {},
  selected_color = {},
  handlers,
  locale,
}) => {
  useEffect(() => logChangeStep('shingles', configuration.slug), []);

  model3dChangeStep('shingles');

  const hiddenProducts = get(configuration, 'products', [])
    .filter(p => p.hidden)
    .map(p => p.uid);

  shingles.map(sg => {
    // SG stands for ShingleGroup
    sg.items = sg.items.filter(s => !hiddenProducts.includes(s.uid));
    return sg;
  });

  return (
    <>
      {!configuration.custom && <WelcomeModal />}
      <div>
        <div css={css.stepHeadingIntro}>
          <HeaderIcon what="defend" stepHeading />

          <h1 className="h2 stepHeading">{t('build_your_roof.roofing_shingles')}</h1>
          <p>{t('build_your_roof.shingles_intro')}</p>
        </div>

        <div>
          <ShingleGroups
            shingles={shingles}
            selected_shingle={selected_shingle}
            selected_color={selected_color}
            handlers={handlers}
            locale={locale}
          />
        </div>
        <div>
          <MoveNext next={getNextStep('shingles')} />
        </div>

        <p>
          <strong>{t('build_your_roof.interested_in_another_line')}</strong>
        </p>
        <p className="small">
          {t('build_your_roof.shingles_wip')}&nbsp;
          <a
            href={PrefixWithLocale('/roofing/shingles', locale.code)}
            target="_blank"
            data-track="learn-more"
            data-track-destination="Pick Your Shingles"
            rel="noreferrer"
          >
            {t('build_your_roof.shingles_pys')}
          </a>
          .
        </p>
      </div>
    </>
  );
};
export const SelectShingles = connectLocation(ConfigurationConnector(rawSelectShingles));

const ShingleGroups = ({ shingles, selected_shingle, selected_color, handlers, locale }) =>
  shingles.map((sc, idx) => (
    <div key={idx} css={css.optionCategories}>
      <h2 className="categoryHeading">{sc.name}</h2>

      <ul>
        <ShingleList
          shingles={sc.items}
          selected_shingle={selected_shingle}
          selected_color={selected_color}
          handlers={handlers}
          locale={locale}
        />
      </ul>
    </div>
  ));

const ShingleList = connectLocation(
  ({ shingles, selected_shingle, selected_color, t, locale, handlers = { handlers } }) => {
    const [expanded, setExpanded] = useState(true);
    const handleShingleSelect = shingle => {
      if (selected_shingle.uid !== shingle.uid) {
        handlers.setShingle(shingle, shingle.colors[0])();
        setExpanded(true);
      } else if (!expanded) {
        setExpanded(true);
      }
    };
    const handleShingleCollapse = shingle => {
      if (selected_shingle.uid === shingle.uid && expanded) {
        setExpanded(false);
      }
    };

    return shingles.map((s, idx) => {
      const shingleExpanded = selected_shingle.uid === s.uid && expanded;

      return (
        <li key={idx} css={shingleExpanded ? css.active_step_item : css.step_item}>
          <div css={css.stepItemContent} onClick={() => handleShingleSelect(s)}>
            <div css={shingleExpanded && css.activeCollapserTarget} onClick={() => handleShingleCollapse(s)}>
              <div css={css.headingInfo}>
                <span css={css.optionName}>{s.name}</span>
                <span className="headingPricingContainer">
                  <span css={css.shinglePrice}>
                    <PriceWidget price={s.price} />
                  </span>
                </span>
              </div>

              <div css={css.dropdown}>
                <IfElse condition={shingleExpanded}>
                  <p>{s.short_description}</p>
                  <p>
                    {_.truncate(s.short_description, {
                      separator: ' ',
                      length: 150,
                    })}
                  </p>
                </IfElse>

                <i className={`fa ${shingleExpanded ? 'fa-chevron-up' : 'fa-chevron-down'}`} />
              </div>
            </div>

            <If condition={s.unavailable}>
              <div css={css.alertSpacing}>
                <AlertMessage
                  title={
                    ['supreme', 'woodmoor', 'woodcrest', 'berkshire'].includes(s.uid)
                      ? t('availability.title')
                      : t('availability.limited_availability_title')
                  }
                  message={t('availability.message')}
                  dismissable={false}
                  type="warning"
                  alertIcon
                  style={unavailableShingleAlertCSS}
                />
              </div>
            </If>

            <div className="info">
              <If condition={!s.unavailable}>
                <p>
                  <i>
                    {s.colors.length} {t('build_your_roof.colors_available')}
                  </i>
                </p>
              </If>

              <If condition={shingleExpanded}>
                <ul css={css.shingleColorsList}>
                  {s.colors.map((c, cidx) => (
                    <li
                      key={cidx}
                      onClick={handlers.setShingle(s, c)}
                      css={_.isEqual(c, selected_color) ? css.active_shingle_color : ''}
                      data-track="tool-select"
                      data-track-option={c.name}
                    >
                      <img src={c.tile_image} alt={altText(c, 'swatch', locale.code, c.name)} />
                      <span className="colorTooltip">{c.name}</span>
                    </li>
                  ))}
                </ul>
                <p>
                  <a
                    href={PrefixWithLocale(`/roofing/shingles/${s.uid}`, locale.code)}
                    target="_blank"
                    data-track="learn-more"
                    data-track-destination={dataTrackAttribute(`/roofing/shingles/${s.uid}`)}
                    rel="noreferrer"
                  >
                    {t('build_your_roof.learn_more')}
                    <i css={css.learnMoreIcon} className="fa fa-external-link" />
                  </a>
                </p>
              </If>
            </div>
          </div>
        </li>
      );
    });
  }
);
